import type { ReactNode } from "react";
import {
  ShieldAlert,
  Lock,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Ban,
  type LucideIcon,
} from "lucide-react";
import { Button, Card } from "@nutrayan/ui";

export type StatusTone = "success" | "warning" | "danger" | "info";

const TONE: Record<StatusTone, { icon: string; ring: string }> = {
  success: { icon: "text-forest", ring: "bg-forest/10" },
  warning: { icon: "text-gold", ring: "bg-gold/10" },
  danger: { icon: "text-terracotta", ring: "bg-terracotta/10" },
  info: { icon: "text-indigo", ring: "bg-indigo/10" },
};

interface Action {
  label: string;
  onClick: () => void;
  variant?: "primary" | "outline" | "ghost";
}

interface Props {
  icon: LucideIcon;
  tone: StatusTone;
  title: string;
  description: ReactNode;
  primaryAction?: Action;
  secondaryAction?: Action;
}

/** Full-screen status card for terminal auth states (locked, suspended, success…). */
export function StatusScreen({
  icon: Icon,
  tone,
  title,
  description,
  primaryAction,
  secondaryAction,
}: Props) {
  const t = TONE[tone];
  return (
    <div className="bg-sand flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md text-center">
        <div
          className={`mx-auto flex h-14 w-14 items-center justify-center rounded-full ${t.ring}`}
        >
          <Icon size={26} className={t.icon} aria-hidden />
        </div>
        <h1 className="font-display text-display-md text-charcoal mt-4">{title}</h1>
        <div className="text-body-sm text-ink mt-2">{description}</div>
        {primaryAction || secondaryAction ? (
          <div className="mt-6 flex flex-col gap-2">
            {primaryAction ? (
              <Button
                size="lg"
                fullWidth
                variant={primaryAction.variant ?? "primary"}
                onClick={primaryAction.onClick}
              >
                {primaryAction.label}
              </Button>
            ) : null}
            {secondaryAction ? (
              <Button
                size="md"
                fullWidth
                variant={secondaryAction.variant ?? "ghost"}
                onClick={secondaryAction.onClick}
              >
                {secondaryAction.label}
              </Button>
            ) : null}
          </div>
        ) : null}
      </Card>
    </div>
  );
}

// Convenience presets used across auth screens.
export const StatusIcons = {
  locked: Lock,
  suspended: Ban,
  unauthorized: ShieldAlert,
  sessionExpired: Clock,
  success: CheckCircle2,
  error: AlertTriangle,
} satisfies Record<string, LucideIcon>;
